import {
  AXIAL_DIRECTIONS,
  addCoords,
  coordKey,
  offsetKey,
  offsetToAxial,
} from "./hex";
import type {
  ActionDefinition,
  ActionId,
  Cell,
  CellKnowledge,
  CropDefinition,
  CropId,
  EvidenceItem,
  JournalLayer,
  JournalState,
  LayerScore,
  LedgerEntry,
  PointOfInterest,
  RawSite,
  Site,
  SiteResult,
  SoilKind,
  SurveyResult,
  TerminalActionId,
  TerminalResult,
} from "./types";

export const CROPS: Readonly<Record<CropId, CropDefinition>> = {
  oats: {
    id: "oats",
    name: "Hardy oats",
    shortName: "O",
    cost: 4,
    role: "Reliable local reading",
    description:
      "Tolerant grain. Modest returns, but its growth clearly reflects the surface soil.",
    yieldBySoil: {
      "wet-silt": 3,
      "floodplain-loam": 7,
      "ordinary-loam": 5,
      "compacted-earth": 2,
      "shallow-rock": 2,
    },
  },
  wheat: {
    id: "wheat",
    name: "Market wheat",
    shortName: "W",
    cost: 7,
    role: "High-risk production",
    description:
      "Valuable on deep fertile soil, but very poor on wet, rocky, or compacted ground.",
    yieldBySoil: {
      "wet-silt": 1,
      "floodplain-loam": 14,
      "ordinary-loam": 8,
      "compacted-earth": 2,
      "shallow-rock": 1,
    },
  },
  alfalfa: {
    id: "alfalfa",
    name: "Deep-rooted alfalfa",
    shortName: "A",
    cost: 5,
    role: "Biological investigation",
    description:
      "Low-return indicator planting. Deep roots reveal buried resistance and nearby moisture.",
    yieldBySoil: {
      "wet-silt": 2,
      "floodplain-loam": 6,
      "ordinary-loam": 4,
      "compacted-earth": 1,
      "shallow-rock": 1,
    },
  },
};

export const ACTIONS: Readonly<Record<ActionId, ActionDefinition>> = {
  oats: {
    id: "oats",
    name: CROPS.oats.name,
    cost: CROPS.oats.cost,
    description: CROPS.oats.description,
    terminal: true,
  },
  wheat: {
    id: "wheat",
    name: CROPS.wheat.name,
    cost: CROPS.wheat.cost,
    description: CROPS.wheat.description,
    terminal: true,
  },
  alfalfa: {
    id: "alfalfa",
    name: CROPS.alfalfa.name,
    cost: CROPS.alfalfa.cost,
    description: CROPS.alfalfa.description,
    terminal: true,
  },
  survey: {
    id: "survey",
    name: "Low-impact survey",
    cost: 9,
    description:
      "Keeps the location available while revealing exact soil, deep resistance, and nearby patterns.",
    terminal: false,
  },
  excavate: {
    id: "excavate",
    name: "Full excavation",
    cost: 12,
    description:
      "A terminal, disruptive investigation with definitive evidence and a possible discovery reward.",
    terminal: true,
  },
  preserve: {
    id: "preserve",
    name: "Preserve location",
    cost: 8,
    description:
      "A terminal commitment. Valuable land may receive a grant or purchase offer; ordinary land earns nothing.",
    terminal: true,
  },
};

export const TERMINAL_ACTION_IDS: readonly TerminalActionId[] = [
  "oats",
  "wheat",
  "alfalfa",
  "excavate",
  "preserve",
];

export const MINIMUM_TERMINAL_COST = Math.min(
  ...Object.values(ACTIONS)
    .filter((action) => action.terminal)
    .map((action) => action.cost),
);

const soilNames: Readonly<Record<SoilKind, string>> = {
  "wet-silt": "Saturated fine silt",
  "floodplain-loam": "Deep alluvial loam",
  "ordinary-loam": "Ordinary brown loam",
  "compacted-earth": "Compacted worked earth",
  "shallow-rock": "Thin soil over angular rock",
};

export function soilName(soil: SoilKind): string {
  return soilNames[soil];
}

function setFromOffsets(offsets: readonly (readonly [number, number])[]): Set<string> {
  return new Set(offsets.map(offsetKey));
}

function neighborsOf(cell: Cell, site: Site): readonly Cell[] {
  return AXIAL_DIRECTIONS.map((direction) =>
    site.cellsByKey.get(coordKey(addCoords(cell.coord, direction))),
  ).filter((neighbor): neighbor is Cell => neighbor !== undefined);
}

function surfaceFor(
  isWater: boolean,
  isFloodplain: boolean,
  isRoad: boolean,
  isRocky: boolean,
): Cell["surface"] {
  if (isWater) return "reed-swale";
  if (isRocky) return "stony-rise";
  if (isRoad) return "short-grass";
  if (isFloodplain) return "lush-grass";
  return "meadow";
}

function observationFor(
  isWater: boolean,
  isFloodplain: boolean,
  isRoad: boolean,
  isRocky: boolean,
  pointId: string | undefined,
): string {
  if (pointId === "stone-bridge") {
    return "A shallow grassy hollow meets a faint, unusually straight strip.";
  }
  if (pointId === "farmstead-cellar") {
    return "Short grass surrounds a lone lilac and a barely perceptible rectangular rise.";
  }
  if (pointId === "stone-lined-well") {
    return "Thistles grow in a compact circle beside the greener low ground.";
  }
  if (isWater) {
    return "Rushes and darker grass occupy a shallow, damp swale.";
  }
  if (isRocky) {
    return "Pale grass grows around several angular stones on slightly higher ground.";
  }
  if (isRoad) {
    return "The grass is shorter here, forming part of a subtle straight band.";
  }
  if (isFloodplain) {
    return "Thick, dark grass grows more vigorously than in the surrounding meadow.";
  }
  return "Mixed meadow grasses cover gently uneven ground.";
}

export function createSite(raw: RawSite): Site {
  const height = raw.elevations.length;
  const width = raw.elevations[0]?.length ?? 0;
  const watercourse = setFromOffsets(raw.watercourse);
  const road = setFromOffsets(raw.road);
  const rocky = setFromOffsets(raw.rocky);
  const pointByKey = new Map(
    raw.pointsOfInterest.map((point) => [offsetKey(point.at), point]),
  );
  const preserveByKey = new Map(
    raw.ecologicalPreserves.map((preserve) => [offsetKey(preserve.at), preserve]),
  );

  const provisional = raw.elevations.flatMap((rowValues, row) =>
    rowValues.map((elevation, column) => {
      const coord = offsetToAxial([column, row]);
      return {
        key: coordKey(coord),
        coord,
        column,
        row,
        elevation,
      };
    }),
  );
  const provisionalByKey = new Map(provisional.map((cell) => [cell.key, cell]));
  const floodplain = new Set<string>();

  for (const key of watercourse) {
    const waterCell = provisionalByKey.get(key);
    if (waterCell === undefined) continue;
    for (const direction of AXIAL_DIRECTIONS) {
      const neighborKey = coordKey(addCoords(waterCell.coord, direction));
      if (provisionalByKey.has(neighborKey) && !watercourse.has(neighborKey)) {
        floodplain.add(neighborKey);
      }
    }
  }

  const cells: Cell[] = provisional.map((entry) => {
    const isWater = watercourse.has(entry.key);
    const isRoad = road.has(entry.key);
    const isRocky = rocky.has(entry.key);
    const isFloodplain = floodplain.has(entry.key);
    const point = pointByKey.get(entry.key);
    const ecologicalPreserve = preserveByKey.get(entry.key);
    const watercoursePreserve = isWater
      ? {
          title: "Former-watercourse conservation easement",
          reward: 13,
        }
      : undefined;
    const preservationTitle =
      point?.title ??
      ecologicalPreserve?.title ??
      watercoursePreserve?.title;
    const preservationReward =
      point?.preservationReward ??
      ecologicalPreserve?.reward ??
      watercoursePreserve?.reward ??
      0;

    let soil: SoilKind = "ordinary-loam";
    if (isFloodplain) soil = "floodplain-loam";
    if (isRoad) soil = "compacted-earth";
    if (isRocky) soil = "shallow-rock";
    if (isWater) soil = "wet-silt";

    const optionalPoint =
      point === undefined ? {} : { pointOfInterestId: point.id };
    const optionalPreservation =
      preservationTitle === undefined ? {} : { preservationTitle };

    return {
      ...entry,
      label: `${String.fromCharCode(65 + entry.column)}${entry.row + 1}`,
      surface: surfaceFor(isWater, isFloodplain, isRoad, isRocky),
      surfaceObservation: observationFor(
        isWater,
        isFloodplain,
        isRoad,
        isRocky,
        point?.id,
      ),
      soil,
      isFormerWatercourse: isWater,
      isOldRoad: isRoad,
      isRocky,
      preservationReward,
      ...optionalPoint,
      ...optionalPreservation,
    };
  });

  const pointsOfInterest: PointOfInterest[] = raw.pointsOfInterest.map((point) => ({
    id: point.id,
    title: point.title,
    cellKey: offsetKey(point.at),
    description: point.description,
    excavationReward: point.excavationReward,
    preservationReward: point.preservationReward,
  }));

  return {
    schemaVersion: raw.schemaVersion,
    rulesetVersion: raw.rulesetVersion,
    contentVersion: raw.contentVersion,
    id: raw.id,
    title: raw.title,
    subtitle: raw.subtitle,
    introduction: raw.introduction,
    width,
    height,
    cells,
    cellsByKey: new Map(cells.map((cell) => [cell.key, cell])),
    watercoursePath: raw.watercourse.map(offsetKey),
    roadPath: raw.road.map(offsetKey),
    pointsOfInterest,
    pointsOfInterestById: new Map(
      pointsOfInterest.map((point) => [point.id, point]),
    ),
    startingFunds: raw.startingFunds,
    historicalAccount: raw.historicalAccount,
  };
}

export function validateSite(site: Site): readonly string[] {
  const issues: string[] = [];
  if (site.width < 1 || site.height < 1) {
    issues.push("Site dimensions must be positive.");
  }
  if (site.cells.length !== site.width * site.height) {
    issues.push("Site must contain one cell for every row and column.");
  }
  if (site.cellsByKey.size !== site.cells.length) {
    issues.push("Every site cell must have a unique coordinate.");
  }
  for (const point of site.pointsOfInterest) {
    if (!site.cellsByKey.has(point.cellKey)) {
      issues.push(`Point of interest ${point.id} is outside the site.`);
    }
    if (point.excavationReward <= ACTIONS.excavate.cost) {
      issues.push(`Point of interest ${point.id} must repay a correct excavation.`);
    }
    if (point.preservationReward <= ACTIONS.preserve.cost) {
      issues.push(`Point of interest ${point.id} must repay correct preservation.`);
    }
  }
  if (new Set(site.pointsOfInterest.map((point) => point.id)).size !== site.pointsOfInterest.length) {
    issues.push("Point-of-interest identifiers must be unique.");
  }
  for (const [difficulty, funds] of Object.entries(site.startingFunds)) {
    if (!Number.isInteger(funds) || funds < MINIMUM_TERMINAL_COST) {
      issues.push(`${difficulty} starting funds must permit a terminal action.`);
    }
  }
  return issues;
}

function localSoilEvidence(cell: Cell): EvidenceItem {
  const details: Readonly<Record<SoilKind, string>> = {
    "wet-silt":
      "Fine gray-brown sediment stayed saturated around the roots.",
    "floodplain-loam":
      "The plot contains unusually deep, dark, stone-free loam.",
    "ordinary-loam":
      "The root zone contains ordinary brown soil with no strong anomaly.",
    "compacted-earth":
      "Roots flattened and turned along a dense, level layer of worked earth.",
    "shallow-rock":
      "Angular stone and shallow bedrock restricted the root zone.",
  };
  return {
    title: soilNames[cell.soil],
    detail: details[cell.soil],
  };
}

function deepEvidence(cell: Cell): EvidenceItem {
  if (cell.pointOfInterestId !== undefined) {
    return {
      title: "Buried resistance",
      detail:
        "The deep profile contains a regular, stone-like obstruction unlike natural bedrock.",
    };
  }
  if (cell.isRocky) {
    return {
      title: "Shallow bedrock",
      detail: "Natural angular rock rises close to the surface.",
    };
  }
  if (cell.isFormerWatercourse) {
    return {
      title: "Layered water-laid sediment",
      detail: "Alternating fine silt and rounded grains continue below the root zone.",
    };
  }
  if (cell.isOldRoad) {
    return {
      title: "Deep compaction",
      detail: "The compressed layer is broad, level, and consistent with repeated traffic.",
    };
  }
  return {
    title: "Unobstructed subsoil",
    detail: "The soil deepens normally without masonry, bedrock, or saturated layers.",
  };
}

function neighborhoodEvidence(cell: Cell, site: Site): readonly EvidenceItem[] {
  const neighborhood = [cell, ...neighborsOf(cell, site)];
  const wetCount = neighborhood.filter(
    (entry) =>
      entry.soil === "wet-silt" || entry.soil === "floodplain-loam",
  ).length;
  const workedCount = neighborhood.filter((entry) => entry.isOldRoad).length;
  return [
    {
      title: "Moisture influence",
      detail: `${wetCount} of this location and its neighboring locations show a water-influenced soil profile.`,
    },
    {
      title: "Worked-ground pattern",
      detail: `${workedCount} of this location and its neighboring locations contain the same compacted linear signature.`,
    },
  ];
}

export function resolveSurvey(cell: Cell, site: Site): SurveyResult {
  const preservationEvidence: EvidenceItem =
    cell.preservationReward > 0
      ? {
          title: "Preservation value identified",
          detail: `${cell.preservationTitle ?? "This location"} qualifies for ${cell.preservationReward} funds if kept undisturbed.`,
        }
      : {
          title: "No preservation award identified",
          detail:
            "The assessment found no recognized ecological, geological, or historical preservation program for this location.",
        };
  return {
    cost: ACTIONS.survey.cost,
    evidence: [
      localSoilEvidence(cell),
      deepEvidence(cell),
      ...neighborhoodEvidence(cell, site),
      preservationEvidence,
    ],
  };
}

function cropEvidence(
  cropId: CropId,
  cell: Cell,
  site: Site,
): readonly EvidenceItem[] {
  if (cropId === "oats" || cropId === "wheat") {
    const evidence = [localSoilEvidence(cell)];
    if (cell.pointOfInterestId !== undefined) {
      evidence.push({
        title: "Irregular growth",
        detail:
          "A compact patch within the plot performed worse than the surrounding soil, suggesting a buried obstruction.",
      });
    }
    return evidence;
  }

  return [
    localSoilEvidence(cell),
    deepEvidence(cell),
    neighborhoodEvidence(cell, site)[0] as EvidenceItem,
  ];
}

function cropOutcomeTitle(crop: CropDefinition, revenue: number): string {
  const net = revenue - crop.cost;
  if (net >= 5) return `${crop.name} flourished`;
  if (net > 0) return `${crop.name} produced a profit`;
  if (net === 0) return `${crop.name} broke even`;
  return `${crop.name} struggled`;
}

export function resolveTerminalAction(
  action: TerminalActionId,
  cell: Cell,
  site: Site,
): TerminalResult {
  if (action === "oats" || action === "wheat" || action === "alfalfa") {
    const crop = CROPS[action];
    const baseRevenue = crop.yieldBySoil[cell.soil];
    const obstructionPenalty =
      cell.pointOfInterestId !== undefined && action === "alfalfa" ? 1 : baseRevenue;
    const revenue = Math.min(baseRevenue, obstructionPenalty);
    return {
      action,
      cost: crop.cost,
      revenue,
      siteValue: revenue,
      outcomeTitle: cropOutcomeTitle(crop, revenue),
      outcomeDetail: `${crop.name} returned ${revenue} funds after a planting cost of ${crop.cost}.`,
      evidence: cropEvidence(action, cell, site),
    };
  }

  const point =
    cell.pointOfInterestId === undefined
      ? undefined
      : site.pointsOfInterestById.get(cell.pointOfInterestId);

  if (action === "excavate") {
    if (point !== undefined) {
      return {
        action,
        cost: ACTIONS.excavate.cost,
        revenue: point.excavationReward,
        siteValue: point.excavationReward,
        outcomeTitle: point.title,
        outcomeDetail: `${point.description} Documentation and recovery produced ${point.excavationReward} funds.`,
        evidence: [
          localSoilEvidence(cell),
          deepEvidence(cell),
          {
            title: "Confirmed historical feature",
            detail: point.description,
          },
        ],
        discoveryId: point.id,
      };
    }
    return {
      action,
      cost: ACTIONS.excavate.cost,
      revenue: 0,
      siteValue: 0,
      outcomeTitle: "Disturbed ground — no point of interest",
      outcomeDetail:
        "The excavation found no valuable structure or deposit. The location remains terminal, but its full profile is now known.",
      evidence: [
        localSoilEvidence(cell),
        deepEvidence(cell),
        ...neighborhoodEvidence(cell, site),
      ],
    };
  }

  if (cell.preservationReward > 0) {
    const discoveryId = point?.id;
    const optionalDiscovery =
      discoveryId === undefined ? {} : { discoveryId };
    return {
      action,
      cost: ACTIONS.preserve.cost,
      revenue: cell.preservationReward,
      siteValue: cell.preservationReward,
      outcomeTitle: cell.preservationTitle ?? "Valuable preserved land",
      outcomeDetail: `The undisturbed location qualified for ${cell.preservationReward} funds in preservation support.`,
      evidence: [
        localSoilEvidence(cell),
        {
          title: "Preservation value confirmed",
          detail:
            point?.description ??
            "The location's natural condition has recognized ecological or educational value.",
        },
      ],
      ...optionalDiscovery,
    };
  }

  return {
    action,
    cost: ACTIONS.preserve.cost,
    revenue: 0,
    siteValue: 0,
    outcomeTitle: "Ordinary land preserved",
    outcomeDetail:
      "No organization offered support for this location. It remains undisturbed and terminal.",
    evidence: [
      localSoilEvidence(cell),
      {
        title: "No qualifying value",
        detail:
          "The location contains no recognized ecological, geological, or historical preservation target.",
      },
    ],
  };
}

export function maximumCellValue(cell: Cell, site: Site): number {
  return Math.max(
    ...TERMINAL_ACTION_IDS.map(
      (action) => resolveTerminalAction(action, cell, site).siteValue,
    ),
  );
}

export function maximumSiteValue(site: Site): number {
  return site.cells.reduce(
    (total, cell) => total + maximumCellValue(cell, site),
    0,
  );
}

function truthForLayer(site: Site, layer: JournalLayer): ReadonlySet<string> {
  if (layer === "water") {
    return new Set(
      site.cells
        .filter((cell) => cell.isFormerWatercourse)
        .map((cell) => cell.key),
    );
  }
  if (layer === "road") {
    return new Set(
      site.cells.filter((cell) => cell.isOldRoad).map((cell) => cell.key),
    );
  }
  return new Set(
    site.cells
      .filter((cell) => cell.pointOfInterestId !== undefined)
      .map((cell) => cell.key),
  );
}

function scoreLayer(
  site: Site,
  journal: JournalState,
  layer: JournalLayer,
  title: string,
): LayerScore {
  const truth = truthForLayer(site, layer);
  const marked = journal[layer];
  const correct = [...marked].filter((key) => truth.has(key)).length;
  const missed = [...truth].filter((key) => !marked.has(key)).length;
  const incorrect = [...marked].filter((key) => !truth.has(key)).length;
  const union = new Set([...truth, ...marked]).size;
  const accuracy = union === 0 ? 100 : (correct / union) * 100;
  return { layer, title, correct, missed, incorrect, accuracy };
}

function gradeFor(score: number): string {
  if (score >= 92) return "S";
  if (score >= 82) return "A";
  if (score >= 70) return "B";
  if (score >= 58) return "C";
  if (score >= 45) return "D";
  return "E";
}

export function calculateSiteResult(
  site: Site,
  openingFunds: number,
  knowledge: ReadonlyMap<string, CellKnowledge>,
  ledger: readonly LedgerEntry[],
  journal: JournalState,
): SiteResult {
  const totalSpent = ledger.reduce((total, entry) => total + entry.spent, 0);
  const agriculturalRevenue = ledger
    .filter((entry) =>
      entry.action === "oats" ||
      entry.action === "wheat" ||
      entry.action === "alfalfa",
    )
    .reduce((total, entry) => total + entry.earned, 0);
  const discoveryRevenue = ledger
    .filter(
      (entry) => entry.action === "excavate" || entry.action === "preserve",
    )
    .reduce((total, entry) => total + entry.earned, 0);
  const closingFunds =
    openingFunds - totalSpent + agriculturalRevenue + discoveryRevenue;
  const realizedSiteValue = [...knowledge.values()].reduce(
    (total, entry) => total + (entry.terminal?.siteValue ?? 0),
    0,
  );
  const maximumValue = maximumSiteValue(site);
  const siteQuality =
    maximumValue === 0 ? 100 : (realizedSiteValue / maximumValue) * 100;
  const layers = [
    scoreLayer(site, journal, "water", "Former watercourse"),
    scoreLayer(site, journal, "road", "Old road"),
    scoreLayer(site, journal, "interest", "Points of interest"),
  ];
  const historyAccuracy =
    layers.reduce((total, layer) => total + layer.accuracy, 0) / layers.length;
  const discoveryIds = new Set(
    [...knowledge.values()]
      .map((entry) => entry.terminal?.discoveryId)
      .filter((id): id is string => id !== undefined),
  );
  const foundDiscoveries = site.pointsOfInterest.filter((point) =>
    discoveryIds.has(point.id),
  );
  const discoveryRate =
    site.pointsOfInterest.length === 0
      ? 100
      : (foundDiscoveries.length / site.pointsOfInterest.length) * 100;
  const overallScore =
    siteQuality * 0.5 + historyAccuracy * 0.3 + discoveryRate * 0.2;

  return {
    openingFunds,
    totalSpent,
    agriculturalRevenue,
    discoveryRevenue,
    closingFunds,
    netChange: closingFunds - openingFunds,
    realizedSiteValue,
    maximumSiteValue: maximumValue,
    siteQuality,
    historyAccuracy,
    discoveryRate,
    overallScore,
    grade: gradeFor(overallScore),
    layers,
    foundDiscoveries,
    undiscoveredCount: site.pointsOfInterest.length - foundDiscoveries.length,
  };
}
